import {
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "../common/CommonModal";
import CreditLimitCard from "./CreditLimitCard";
import CustomerSearchInput from "./CustomerSearchInput";
import useApi from "../../hooks/useApi";
import CreditCustomerList from "../common/CreditCustomerList";
import CashCustomerList from "./CashCustomerList";
import RegisterWalkinCustomerForm from "./RegisterWalkinCustomer";
import { DOCUMENT_TYPES, getDocumentConfig } from "../config/DocumentConfig";
import { Search, History } from "lucide-react";
import JobCodeList from "../common/JobCodeList";
import checkIfUserHasAccess from "../common/UserCheckAccess";

const SectionTab1 = ({
  invoiceData,
  setInvoiceData,
  todayDate,
  setTodayDate,
  currentTime,
  totals,
  setRecieptSection,
  onAccountSelect,
  accountData,
  setAccountData,
  setAccountNumber,
  areaCodes,
  invoiceNoFromState,
  documentType,
  onRestoreDraft,
  BASE_URL,
  COMPANY_CODE,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "", // 'add', 'edit', 'list', 'cr-list','cash-list'
    customer: null,
  });
  const [salesmanList, setSalesmanList] = useState([]);
  // Helper to get salesman name from code
  const getSalesmanName = (code) => {
    const found = salesmanList.find((s) => s.EMP_ID === code);
    return found ? found.NAME : code;
  };
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [lastSearchQuery, setLastSearchQuery] = useState("");

  const [termsTemplatelist, setTermsTemplatelist] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [isCallingTemplateListApi, setIsCallingTemplateListApi] =
    useState(false);
  const [isCallingAddToTemplateListApi, setIsCallingAddToTemplateListApi] =
    useState(false);
  const [showAddToTemplateForm, setShowAddToTemplateForm] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [templatename, setTemplateName] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(null);
  const [paymentState, setPaymentState] = useState("");

  const searchInputRef = useRef(null);
  const DEPARTMENT_NO =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const CURRENT_USER_OBJECT =
    localStorage.getItem("currentUser")
      ? JSON.parse(localStorage.getItem("currentUser"))
      : null;
  const DEFAULT_SALESMAN = CURRENT_USER_OBJECT[0]?.sales_man || "-";
  // console.log("DEFAULT_SALESMAN", DEFAULT_SALESMAN);

  const { request: requestCreditCustomers, loading: loadingCreditCustomers } =
    useApi();

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [invoiceData.paymentType]);

  const fetchCreditCustomers = async (query = "-") => {
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT_NO}/CUSTOMERS/-/${query}`;

    try {
      const response = await requestCreditCustomers({
        url: url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch credit customers", err);
      return [];
    }
  };

  const { request: requestCashCustomers, loading: loadingCashCustomers } =
    useApi();
  const fetchCashCustomers = async (query = "-") => {
    const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/WALKINLIST/${DEPARTMENT_NO}/ALL/-/${query}`;
    try {
      const response = await requestCashCustomers({
        url: url,
        method: "GET",
        timeout: 1000,
      });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch cash customers", err);
      return [];
    }
  };

  const { request: requestSalesmanList, loading: loadingSalesmanList } =
    useApi();

  const fetchSalesmanList = async () => {
    const url = `${BASE_URL}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${DEPARTMENT_NO}`;
    try {
      const response = await requestSalesmanList({
        url: url,
        method: "GET",
        timeout: 10000,
      });
      console.log("Response", response);
      setSalesmanList(response || []);
    } catch (err) {
      console.error("Error loading data:", err);
      setSalesmanList([]);
    }
  };
  useEffect(() => {
    fetchSalesmanList();
  }, []);

  const getTemplatelist = useCallback(() => {
    setIsCallingTemplateListApi(true);
    const apiUrl = `${BASE_URL}/api/Proposal/${COMPANY_CODE}/TERMSTEMPLATELIST/-/-`;
    axios
      .get(apiUrl)
      .then((res) => {
        setTermsTemplatelist(res.data);
      })
      .catch(() => {
        setTermsTemplatelist(null);
      })
      .finally(() => {
        setIsCallingTemplateListApi(false);
      });
  }, [BASE_URL, COMPANY_CODE]);

  const getTermsTemplateDetails = useCallback(
    (mastercode) => {
      const apiUrl = `${BASE_URL}/api/Proposal/${COMPANY_CODE}/TERMSTEMPLATESINGLE/${mastercode}/-`;
      axios
        .get(apiUrl)
        .then((res) => {
          const match = res.data?.find((item) => item.mastercode === mastercode);
          if (match) {
            setPaymentState(match.description || "");
            setSelectedTemplateName(mastercode);
            setInvoiceData((prev) => ({
              ...prev,
              terms: match.description || "",
            }));
          }
        })
        .catch(() => { });
    },
    [BASE_URL, COMPANY_CODE, setInvoiceData]
  );

  const loadDefaultTemplate = useCallback(async () => {
    try {
      const apiUrl = `${BASE_URL}/api/Proposal/${COMPANY_CODE}/TERMSTEMPLATELIST/-/-`;
      const response = await axios.get(apiUrl);
      const defaultTemplate = response.data?.find(
        (item) => item.mastercode?.trim().toLowerCase() === "default"
      );
      if (defaultTemplate) {
        getTermsTemplateDetails(defaultTemplate.mastercode);
      }
    } catch (error) {
      console.error("Error loading default template:", error);
    }
  }, [BASE_URL, COMPANY_CODE, getTermsTemplateDetails]);

  useEffect(() => {
    if (
      documentType === DOCUMENT_TYPES.QUOTATION
    ) {
      getTemplatelist();
    }
  }, [getTemplatelist, documentType]);

  useEffect(() => {
    if (
      documentType === DOCUMENT_TYPES.QUOTATION &&
      termsTemplatelist &&
      !invoiceData.terms &&
      !selectedTemplateName
    ) {
      loadDefaultTemplate();
    }
  }, [termsTemplatelist, documentType]);

  useEffect(() => {
    if (invoiceData.terms !== undefined && invoiceData.terms !== null) {
      setPaymentState(invoiceData.terms || "");
    }
  }, [invoiceData.terms]);

  const saveToTermsTemplate = () => {
    if (!templatename || templatename.trim().length === 0) {
      alert("Please enter template name");
      return;
    }
    if (!paymentState || paymentState.trim().length === 0) {
      alert("Please enter terms & conditions text");
      return;
    }

    setIsCallingAddToTemplateListApi(true);
    const payload = [
      {
        CmpCode: COMPANY_CODE,
        mode: "ENTRY",
        mastercode: templatename.trim(),
        description: paymentState,
      },
    ];

    axios
      .post(`${BASE_URL}/api/TermsTemplate`, payload, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (res.data.result === "Saved") {
          alert("Template saved successfully");
          setShowAddToTemplateForm(false);
          setTemplateName("");
          setSelectedTemplateName(templatename.trim());
          getTemplatelist();
        } else {
          alert(
            "Something went wrong, please try a different name or try later"
          );
        }
      })
      .catch(() => {
        alert("Something went wrong, please try later");
      })
      .finally(() => {
        setIsCallingAddToTemplateListApi(false);
      });
  };

  const renameTemplate = () => {
    if (!renameValue || renameValue.trim().length === 0) {
      alert("Please enter a new name");
      return;
    }
    if (!selectedTemplateName) {
      alert("No template selected to rename");
      return;
    }
    if (renameValue.trim() === selectedTemplateName) {
      setShowRenameForm(false);
      return;
    }

    setIsCallingAddToTemplateListApi(true);

    const deletePayload = [
      {
        CmpCode: COMPANY_CODE,
        mode: "DELETE",
        mastercode: selectedTemplateName,
        description: "-",
      },
    ];

    axios
      .post(`${BASE_URL}/api/TermsTemplate`, deletePayload, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        const savePayload = [
          {
            CmpCode: COMPANY_CODE,
            mode: "ENTRY",
            mastercode: renameValue.trim(),
            description: paymentState,
          },
        ];
        return axios.post(`${BASE_URL}/api/TermsTemplate`, savePayload, {
          headers: { "Content-Type": "application/json" },
        });
      })
      .then((res) => {
        if (res.data.result === "Saved") {
          alert("Template renamed successfully");
          setSelectedTemplateName(renameValue.trim());
          setShowRenameForm(false);
          setRenameValue("");
          getTemplatelist();
        } else {
          alert("Something went wrong, please try later");
        }
      })
      .catch(() => {
        alert("Something went wrong, please try later");
      })
      .finally(() => {
        setIsCallingAddToTemplateListApi(false);
      });
  };

  const deleteTemplate = (mastercode) => {
    setIsCallingAddToTemplateListApi(true);
    const payload = [
      {
        CmpCode: COMPANY_CODE,
        mode: "DELETE",
        mastercode: mastercode,
        description: "-",
      },
    ];

    axios
      .post(`${BASE_URL}/api/TermsTemplate`, payload, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        if (res.data?.result?.trim()?.toUpperCase() === "SAVED") {
          alert("Template deleted successfully");
          if (selectedTemplateName === mastercode) {
            setSelectedTemplateName("");
            setPaymentState("");
            setInvoiceData((prev) => ({ ...prev, terms: "" }));
          }
          getTemplatelist();
        } else {
          alert("Something went wrong, please try later");
        }
      })
      .catch(() => {
        alert("Failed to delete template");
      })
      .finally(() => {
        setIsCallingAddToTemplateListApi(false);
      });
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setPaymentState(value);
    setInvoiceData((prev) => ({ ...prev, terms: value }));
  };

  const handleTemplateSelect = (e) => {
    const mastercode = e.target.value;
    if (mastercode) {
      getTermsTemplateDetails(mastercode);
    } else {
      setSelectedTemplateName("");
      setPaymentState("");
      setInvoiceData((prev) => ({ ...prev, terms: "" }));
    }
  };

  // useEffect(() => {
  //   if (
  //     salesmanList.length > 0 &&
  //     !invoiceNoFromState &&
  //     !invoiceData.salesman
  //   ) {
  //     const matched = salesmanList.find((s) => s.EMP_ID === DEFAULT_SALESMAN);
  //     setInvoiceData((prev) => ({
  //       ...prev,
  //       salesman: matched ? matched.EMP_ID : salesmanList[0].EMP_ID,
  //     }));
  //   }
  // }, [salesmanList, invoiceNoFromState]);
  useEffect(() => {
    if (
      salesmanList.length > 0 &&
      !invoiceNoFromState &&
      !invoiceData.salesman
    ) {
      // Only auto-select if a valid default salesman is provided
      if (DEFAULT_SALESMAN && DEFAULT_SALESMAN !== "-") {
        const matched = salesmanList.find((s) => s.EMP_ID === DEFAULT_SALESMAN);
        if (matched) {
          setInvoiceData((prev) => ({
            ...prev,
            salesman: matched.EMP_ID,
          }));
        }
      }
      // If DEFAULT_SALESMAN is "-" or missing, it will bypass this 
      // and remain blank (""), displaying "Select Salesman..."
    }
  }, [salesmanList, invoiceNoFromState, DEFAULT_SALESMAN, invoiceData.salesman, setInvoiceData]);

  const fetchSuggestions = useCallback(async () => {
    const freetext = searchQuery.trim() || "-";

    if (invoiceData.paymentType === "cash" && searchQuery) {
      setSuggestions([]);

      const result = await fetchCashCustomers(freetext);
      console.log("Suggestion from API Response", result);
      setSuggestions(result || []);
      setShowSuggestions(true);
    } else if (invoiceData.paymentType === "credit" && searchQuery) {
      setSuggestions([]);
      const result = await fetchCreditCustomers(freetext);
      setSuggestions(result || []);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, invoiceData.paymentType]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [fetchSuggestions]);

  useEffect(() => {
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchQuery("");
  }, [invoiceData.paymentType]);

  //  F2 key press to open new add customer modal(cash) or registered customer list(credit)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        // Only open modal if search input is focused
        if (
          searchInputRef.current &&
          document.activeElement === searchInputRef.current
        ) {
          e.preventDefault();
          if (invoiceData.paymentType === "credit") {
            setModalState({
              isOpen: true,
              type: "cr-list",
              customer: null,
            });
          } else if (invoiceData.paymentType === "cash") {
            setModalState({
              isOpen: true,
              type: "cash-list",
              customer: null,
            });
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [invoiceData.paymentType]);

  const handleCustomerList = () => {
    if (invoiceData.paymentType === "credit") {
      setModalState({
        isOpen: true,
        type: "cr-list",
        customer: null,
      });
    } else if (invoiceData.paymentType === "cash") {
      setModalState({
        isOpen: true,
        type: "cash-list",
        customer: null,
      });
    }
  };
  const handleCustomerSelect = (customer) => {
    const isCashCustomer = invoiceData.paymentType === "cash";
    const accountIdentifier = isCashCustomer
      ? customer.AutoId
      : customer.ACCOUNT || customer.accountNumber || customer.account;

    if (!accountIdentifier) {
      console.warn("Missing account identifier for customer", customer);
      return;
    }

    onAccountSelect?.(accountIdentifier, invoiceData.paymentType);

    setSearchQuery("");
    setLastSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
    closeModal();

    // Focus on the code field of the first row in the invoice table
    setTimeout(() => {
      requestAnimationFrame(() => {
        const firstRowCodeInput = document.querySelector(
          '[data-field="code"] input',
        );
        if (firstRowCodeInput) {
          firstRowCodeInput.focus();
        }
      });
    }, 100);
  };

  useEffect(() => {
    // Default VAT status based on company code
    const defaultVatStatus =
      COMPANY_CODE === "SHENGEE" ? "0% VAT" : invoiceData.vatStatus;

    setInvoiceData((prev) => ({
      ...prev,
      vatStatus: defaultVatStatus,
    }));
  }, [invoiceData.paymentType, COMPANY_CODE]);

  const handleAddCustomer = () => {
    setLastSearchQuery(searchQuery);
    setModalState({
      isOpen: true,
      type: "add",
      customer: { contactName: searchQuery },
    });
  };

  const { request: requestSaveCustomer } = useApi();

  const handleSaveCustomer = async (customerData) => {
    console.log("OnSave", customerData);
    try {
      const url = `${BASE_URL}/api/CRMAcmastCRM`;
      const response = await requestSaveCustomer({
        url: url,
        method: "POST",
        data: customerData,
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        timeout: 5000,
      });

      console.log("Customer Saved Sucessfully", response);
      closeModal();
    } catch (error) {
      console.error(
        "Upload Error:",
        error.response ? error.response.data : error.message,
      );
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: "",
      customer: null,
    });
    setSearchQuery(lastSearchQuery);
    setTimeout(() => {
      if (lastSearchQuery) {
        fetchSuggestions();
      }
    }, 0);
  };
  const config = getDocumentConfig(documentType);

  const fieldMap = config.fieldMappings;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
        <div className="lg:col-span-4  col-span-1 ">
          <div className="flex lg:flex-row flex-col items-center  lg:justify-between lg:gap-2">
            <FormControl
              sx={{ display: "flex", alignItems: "center", gap: "0" }}
            >
              <RadioGroup
                row
                name="paymentType"
                value={invoiceData.paymentType}
                onChange={(e) => {
                  const newPaymentType = e.target.value;
                  setInvoiceData({
                    ...invoiceData,
                    paymentType: newPaymentType,
                    cashMode: false,
                    selectedCustomer: null,
                  });
                  setSearchQuery("");
                  setHighlightedIndex(-1);
                  setAccountData(null);
                  setRecieptSection(newPaymentType === "cash");
                }}
              >
                <FormControlLabel
                  value="cash"
                  control={<Radio size="small" />}
                  label="Walk-In "
                  checked={invoiceData.paymentType === "cash"}
                />
                <FormControlLabel
                  value="credit"
                  control={<Radio size="small" />}
                  label="Registered"
                  checked={invoiceData.paymentType === "credit"}
                />
              </RadioGroup>
            </FormControl>
            <div className="flex items-center justify-between lg:gap-3 gap-2 flex-nowrap">
              {config.features.showCashCheckBox && (
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      invoiceData.paymentType === "cash" || invoiceData.cashMode
                    }
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        cashMode: e.target.checked,
                      })
                    }
                    disabled={invoiceData.paymentType === "cash"}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 font-semibold">
                    CASH
                  </span>
                </label>
              )}
              <div className="flex items-center lg:gap-1 flex-shrink-0">
                <span className="text-sm">
                  {documentType === DOCUMENT_TYPES.SALES_INVOICE
                    ? "Inv no"
                    : documentType === DOCUMENT_TYPES.SALES_ORDER
                      ? "SO no"
                      : documentType === DOCUMENT_TYPES.QUOTATION
                        ? "QUOT no"
                        : documentType === DOCUMENT_TYPES.SALES_RETURN
                          ? "SR no"
                          : "Do no"}
                  :
                </span>
                <span className="text-red-500 font-bold truncate max-w-[120px] lg:max-w-[200px]">
                  {invoiceData[fieldMap.documentNo] || invoiceData.documentNo}
                </span>
              </div>
            </div>
          </div>
          <CustomerSearchInput
            searchInputRef={searchInputRef}
            invoiceData={invoiceData}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            suggestions={suggestions}
            setHighlightedIndex={setHighlightedIndex}
            highlightedIndex={highlightedIndex}
            handleAddCustomer={handleAddCustomer}
            handleCustomerSelect={handleCustomerSelect}
            showSuggestions={showSuggestions}
            setInvoiceData={setInvoiceData}
            accountData={accountData}
            setAccountData={setAccountData}
            loadingCashCustomers={loadingCashCustomers}
            loadingCreditCustomers={loadingCreditCustomers}
            setAccountNumber={setAccountNumber}
            handleCustomerList={handleCustomerList}
          />

          <div className=" space-y-3 mt-1">
            <div className="grid grid-cols-2 items-center gap-3">
              {documentType === DOCUMENT_TYPES.QUOTATION ? (
                <>
                  <TextField
                    label="Subject"
                    name="subject"
                    value={invoiceData.subject || ""}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, subject: e.target.value })
                    }
                    placeholder="Subject"
                    fullWidth
                    variant="standard"
                    autoComplete="off"
                    size="small"
                  />
                  <TextField
                    label="Attention"
                    name="attention"
                    value={invoiceData.nextAction || ""}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, nextAction: e.target.value })
                    }
                    placeholder="Attention"
                    fullWidth
                    variant="standard"
                    autoComplete="off"
                    size="small"
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="LPO No"
                    name="Lpono"
                    // required
                    value={invoiceData.lpo_no}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, lpo_no: e.target.value })
                    }
                    placeholder="LPO No"
                    fullWidth
                    variant="standard"
                    // id="filled"
                    autoComplete="off"
                    size="small"
                  />
                  <TextField
                    label="TRN"
                    name="trn"
                    value={invoiceData?.governor || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setInvoiceData((prev) => ({
                        ...prev,
                        governor: value,
                        governorModified: true,
                      }));
                    }}
                    placeholder="TRN"
                    fullWidth
                    variant="standard"
                    // id="filled"
                    autoComplete="off"
                    size="small"
                    inputProps={{ maxLength: 15 }}
                    error={
                      invoiceData?.governor && invoiceData.governor.length !== 15
                    }
                    helperText={
                      invoiceData?.governor && invoiceData.governor.length !== 15
                        ? "TRN must be exactly 15 characters"
                        : ""
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 col-span-1 lg:px-3">
          <div className="flex flex-col gap-3 ">
            {documentType === DOCUMENT_TYPES.QUOTATION ? (
              <>
                {/* QUOTATION: Date + VAT Applied */}
                <div className="grid grid-cols-2 gap-5 items-center text-sm">
                  <div className="flex flex-col">
                    <label className="mb-1 font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={invoiceData.date || ""}
                      onChange={(e) => {
                        setInvoiceData((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }));
                      }}
                      className="px-3 py-2  border border-gray-300  border-b-gray-500 bg-gray-100 rounded-b-none rounded-t-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="VAT Applied"
                    value={invoiceData.vatStatus}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, vatStatus: e.target.value })
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="VAT Applied">VAT Applied</MenuItem>
                    <MenuItem value="0% VAT">0% VAT</MenuItem>
                    <MenuItem value="VAT Exempted">VAT Exempted</MenuItem>
                    <MenuItem value="Out of Scope">Out of Scope</MenuItem>
                  </TextField>
                </div>

                {/* QUOTATION: Project (full width) */}
                <div className="grid grid-cols-2 items-center gap-5 text-sm">
                  <TextField
                    label="Project"
                    name="project"
                    value={invoiceData.project}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        project: e.target.value,
                      })
                    }
                    fullWidth
                    variant="filled"
                    size="small"
                    autoComplete="off"
                  />

                  {/* QUOTATION: Salesman (full width) */}
                  <TextField
                    select
                    fullWidth
                    label="Salesman"
                    variant="filled"
                    size="small"
                    value={invoiceData.salesman || ""}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, salesman: e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Select Salesman...</em>
                    </MenuItem>

                    {salesmanList?.map((salesman) => (
                      <MenuItem key={salesman.EMP_ID} value={salesman.EMP_ID}>
                        {salesman.NAME}
                      </MenuItem>
                    ))}

                    {invoiceData.salesman &&
                      !salesmanList.some(
                        (s) => s.EMP_ID === invoiceData.salesman,
                      ) && (
                        <MenuItem value={invoiceData.salesman} disabled>
                          {getSalesmanName(invoiceData.salesman)} (Unknown)
                        </MenuItem>
                      )}
                  </TextField>
                </div>
                {/* QUOTATION: Delivery + Validity */}
                <div className="grid grid-cols-2 items-center gap-5 text-sm">
                  <TextField
                    label="Delivery"
                    name="delivery"
                    value={invoiceData.delivery || ""}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        delivery: e.target.value,
                      })
                    }
                    placeholder="Delivery"
                    fullWidth
                    variant="filled"
                    size="small"
                    autoComplete="off"
                  />
                  <TextField
                    label="Validity"
                    name="validity"
                    value={invoiceData.validity || ""}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        validity: e.target.value,
                      })
                    }
                    placeholder="Validity"
                    fullWidth
                    variant="filled"
                    size="small"
                    autoComplete="off"
                  />
                </div>
              </>
            ) : (
              <>
                {/* DEFAULT: Date & Due Date */}
                <div className="grid  grid-cols-2 gap-5 items-center text-sm">
                  <div className="flex flex-col">
                    <label className="mb-1 font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={invoiceData.date || ""}
                      onChange={(e) => {
                        setInvoiceData((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }));
                      }}
                      className="px-3 py-2  border border-gray-300  border-b-gray-500 bg-gray-100 rounded-b-none rounded-t-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 font-medium text-gray-700">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={todayDate}
                      onChange={(e) =>
                        setInvoiceData({ ...invoiceData, duedate: e.target.value })
                      }
                      className="px-3 py-2  border border-gray-300  border-b-gray-500 bg-gray-100 rounded-b-none rounded-t-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* DEFAULT: Job + Project */}
                <div className=" grid grid-cols-2 items-center gap-5 text-sm">
                  <TextField
                    label="Job"
                    name="job"
                    value={invoiceData.job}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        job: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "F2") {
                        e.preventDefault();
                        setModalState({
                          isOpen: true,
                          type: "job-list",
                        });
                      }
                    }}
                    fullWidth
                    variant="filled"
                    size="small"
                    autoComplete="off"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setModalState({
                                isOpen: true,
                                type: "job-list",
                              })
                            }
                            size="small"
                            sx={{
                              padding: 0.3,
                            }}
                          >
                            <Search className=" text-gray-500" size={16} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Project"
                    name="project"
                    value={invoiceData.project}
                    onChange={(e) =>
                      setInvoiceData({
                        ...invoiceData,
                        project: e.target.value,
                      })
                    }
                    fullWidth
                    variant="filled"
                    size="small"
                    autoComplete="off"
                  />
                </div>

                {/* DEFAULT: VAT Status + Salesman */}
                <div className=" grid grid-cols-2 gap-5 items-center">
                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="VAT Status"
                    value={invoiceData.vatStatus}
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, vatStatus: e.target.value })
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="VAT Applied">VAT Applied</MenuItem>
                    <MenuItem value="0% VAT">0% VAT</MenuItem>
                    <MenuItem value="VAT Exempted">VAT Exempted</MenuItem>
                    <MenuItem value="Out of Scope">Out of Scope</MenuItem>
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Salesman"
                    variant="filled"
                    size="small"
                    value={invoiceData.salesman || ""} // Ensure it defaults safely to an empty string if null/undefined
                    onChange={(e) =>
                      setInvoiceData({ ...invoiceData, salesman: e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Select Salesman...</em>
                    </MenuItem>

                    {salesmanList?.map((salesman) => (
                      <MenuItem key={salesman.EMP_ID} value={salesman.EMP_ID}>
                        {salesman.NAME}
                      </MenuItem>
                    ))}

                    {/* If current salesman code is not in the list, show it as disabled */}
                    {invoiceData.salesman &&
                      !salesmanList.some(
                        (s) => s.EMP_ID === invoiceData.salesman,
                      ) && (
                        <MenuItem value={invoiceData.salesman} disabled>
                          {getSalesmanName(invoiceData.salesman)} (Unknown)
                        </MenuItem>
                      )}
                  </TextField>
                </div>
              </>
            )}
          </div>
        </div>
        {/* {accountData && ( */}
        <div className="lg:col-span-4 col-span-1">
          {documentType === DOCUMENT_TYPES.QUOTATION ? (
            <div className="space-y-3 border p-4 border-l-4 border-blue-600 rounded-xl text-blue-900 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-blue-800">
                  Terms & Conditions
                </h3>
                {/* {isCallingTemplateListApi && (
                  <CircularProgress size={14} color="primary" />
                )} */}
                <select
                  value={selectedTemplateName}
                  onChange={handleTemplateSelect}
                  className="flex px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                >
                  <option value="">-- Select --</option>
                  {termsTemplatelist &&
                    termsTemplatelist.map((item) => (
                      <option key={item.mastercode} value={item.mastercode}>
                        {item.mastercode}
                      </option>
                    ))}
                </select>
              </div>

              {/* <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-blue-700 whitespace-nowrap">
                  Template:
                </label>

              </div> */}

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => {
                    setShowAddToTemplateForm((prev) => !prev);
                    setShowRenameForm(false);
                  }}
                  className="px-2 py-1 rounded text-[11px] font-medium text-white bg-blue-500 hover:bg-blue-600 transition"
                >
                  Save New
                </button>
                {selectedTemplateName && (
                  <>
                    <button
                      onClick={() => {
                        setShowRenameForm((prev) => !prev);
                        setShowAddToTemplateForm(false);
                        setRenameValue(selectedTemplateName);
                      }}
                      className="px-2 py-1 rounded text-[11px] font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-300 transition"
                    >
                      Rename
                    </button>
                    <button
                      onClick={() =>
                        setConfirmDeleteTemplate(selectedTemplateName)
                      }
                      className="px-2 py-1 rounded text-[11px] font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition"
                    >
                      Delete
                    </button>
                  </>
                )}
                <span className="text-[10px] text-blue-500 italic ml-auto">
                  "default" auto-loads
                </span>
              </div>

              {showAddToTemplateForm && (
                <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border border-blue-200">
                  {isCallingAddToTemplateListApi ? (
                    <CircularProgress size={12} color="primary" />
                  ) : (
                    <>
                      <input
                        placeholder="Template name"
                        value={templatename}
                        onChange={(e) => setTemplateName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        onClick={saveToTermsTemplate}
                        className="px-2 py-1 rounded text-[11px] font-medium text-white bg-green-500 hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setShowAddToTemplateForm(false);
                          setTemplateName("");
                        }}
                        className="px-2 py-1 rounded text-[11px] text-gray-600 bg-gray-100 hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}

              {showRenameForm && (
                <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border border-yellow-300">
                  {isCallingAddToTemplateListApi ? (
                    <CircularProgress size={12} color="primary" />
                  ) : (
                    <>
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-yellow-500"
                      />
                      <button
                        onClick={renameTemplate}
                        className="px-2 py-1 rounded text-[11px] font-medium text-white bg-yellow-500 hover:bg-yellow-600"
                      >
                        Rename
                      </button>
                      <button
                        onClick={() => {
                          setShowRenameForm(false);
                          setRenameValue("");
                        }}
                        className="px-2 py-1 rounded text-[11px] text-gray-600 bg-gray-100 hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}

              <textarea
                className="w-full border border-blue-300 resize-none rounded p-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y bg-white"
                style={{ minHeight: "100px" }}
                placeholder="Enter terms & conditions..."
                value={paymentState}
                onChange={handleTextareaChange}
              />
            </div>
          ) : (
            <CreditLimitCard
              totals={totals}
              accountData={accountData}
              documentData={invoiceData}
            />
          )}
        </div>
        {/* )} */}
      </div>

      {/*Common Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={
          modalState.type === "add"
            ? "Add New Customer"
            : modalState.type === "edit"
              ? "Edit Customer"
              : modalState.type === "job-list"
                ? "Select Job Code"
                : "Select Customer"
        }
        size={
          modalState.type === "cr-list"
            ? "xxl"
            : modalState.type === "cash-list"
              ? "xxl"
              : modalState.type === "add"
                ? "xxl"
                : modalState.type === "job-list"
                  ? "xxl"
                  : "md"
        }
        header={modalState.type === "add" ? false : true}
      >
        {modalState.type === "add" && (
          <RegisterWalkinCustomerForm
            onSave={handleSaveCustomer}
            onCancel={closeModal}
            areaCodes={areaCodes}
            customer={modalState.customer}
          />
        )}

        {modalState.type === "cash-list" && (
          <CashCustomerList handleCustomerSelect={handleCustomerSelect} />
        )}
        {modalState.type === "cr-list" && (
          <CreditCustomerList
            handleCustomerSelect={handleCustomerSelect}
            defaultType="Customers"
          />
        )}
        {modalState.type === "job-list" && (
          <JobCodeList
            open={modalState.isOpen}
            onClose={closeModal}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            onSelect={(job) => {
              setInvoiceData({
                ...invoiceData,
                job: job.JobCode,
              });
              closeModal();
            }}
          />
        )}
      </Modal>

      {confirmDeleteTemplate && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Delete Template
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-bold text-red-500">
                {confirmDeleteTemplate}
              </span>
              ? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteTemplate(null)}
                className="px-4 py-2 rounded-md text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteTemplate(confirmDeleteTemplate);
                  setConfirmDeleteTemplate(null);
                }}
                className="px-4 py-2 rounded-md text-sm text-white bg-red-500 hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SectionTab1;
